import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
// ✅ REMOVED: Pagination import (DynamicTable handles it now)
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FilterListIcon from '@mui/icons-material/FilterList';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DynamicTable from '../Dynamic/DynamicTable';
import DynamicModal from '../Dynamic/DynamicModal';
import RegistrationForm from './Form';
import './Production.css';
import { GetApi, PutApi } from "../../Services/api/ApiCall";
import API_URLS from '../../Services/api/action';
import { useUser } from "../../Context/UserContext";
import { colors } from '@mui/material';

const Production = () => {
  const [openForm, setOpenForm] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [productionCenters, setProductionCenters] = useState([]);
  const [productionTypes, setProductionTypes] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionType, setActionType] = useState('create');
  
  // ✅ REMOVED: Server-side pagination states (currentPage, totalPages, totalCount, itemsPerPage)
  
  const { token, authData } = useUser(); 

  // --- State for Approve/Reject Flow ---
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [rejectComment, setRejectComment] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // --- State for View/Preview Modal ---
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewData, setViewData] = useState(null);

  // --- State for Filters ---
  const [filterProductionType, setFilterProductionType] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  
  // Options for dropdowns
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [blockOptions, setBlockOptions] = useState([]);

  // Selected Filters
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [selectedBlocks, setSelectedBlocks] = useState([]);

  // Fetch Master Data for Filters
  const fetchFilterOptions = useCallback(async () => {
      try {
          const [deptRes, distRes, blockRes] = await Promise.all([
              GetApi(API_URLS.Department || "/officers/departments", token),
              GetApi(API_URLS.GetDistricts || "/master/districts/", token),
              GetApi(API_URLS.GetBlocks || "/master/blocks/", token)
          ]);

          setDepartmentOptions(deptRes?.results || deptRes || []);
          setDistrictOptions(distRes?.results || distRes || []);
          setBlockOptions(blockRes?.results || blockRes || []);
      } catch (error) {
          console.error("Error fetching filter options", error);
      }
  }, [token]);

  useEffect(() => {
      fetchFilterOptions();
  }, [fetchFilterOptions]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build Query Params
      const params = new URLSearchParams();
      // ✅ CHANGED: Fetch all records to let DynamicTable handle client-side pagination
      params.append('limit', 10000); 
      
      // --- AUTOMATIC ROLE & ID INJECTION ---
      const userRole = authData?.role;
      if (userRole) {
        params.append('role', userRole);
        
        if (userRole === 'department_admin' && authData?.department_id) {
          params.append('department_id', authData.department_id);
        } else if (userRole === 'district_admin' && authData?.district_id) {
          params.append('district_id', authData.district_id);
        } else if (userRole === 'block_admin' && authData?.block_id) {
          params.append('block_id', authData.block_id);
        }
      }
      // --------------------------------------

      // Append Manual UI Filters
      if (filterProductionType) {
          params.append('production_type', filterProductionType);
      }
      if (filterStatus) {
          params.append('status', filterStatus);
      }
      if (selectedDepartments.length > 0) {
          params.append('department_ids', selectedDepartments.map(d => d.id || d.department_id).join(','));
      }
      if (selectedDistricts.length > 0) {
          params.append('district_ids', selectedDistricts.map(d => d.id || d.district_id).join(','));
      }
      if (selectedBlocks.length > 0) {
          params.append('block_ids', selectedBlocks.map(b => b.id || b.block_id).join(','));
      }

      const typesUrl = `${API_URLS.ProductionCenterType}`;
      const centersUrl = `${API_URLS.ProductionCenter}?${params.toString()}`;
      
      const [typesRes, centersRes] = await Promise.all([
        GetApi(typesUrl, token),
        GetApi(centersUrl, token)
      ]);

      setProductionTypes(typesRes?.results || []);
      
      if (centersRes) {
        setProductionCenters(centersRes.results || []);
        setRegistrations(centersRes.results || []);
      } else {
        setProductionCenters([]);
        setRegistrations([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, authData?.role, authData?.department_id, authData?.district_id, authData?.block_id, filterProductionType, filterStatus, selectedDepartments, selectedDistricts, selectedBlocks]);

  // Refetch when manual filters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- Handlers ---
  
  const handleOpenApproveModal = (id) => {
    setSelectedId(id);
    setApproveModalOpen(true);
  };

  const handleOpenRejectModal = (id) => {
    setSelectedId(id);
    setRejectComment('');
    setRejectModalOpen(true);
  };

  const handleOpenViewModal = (row) => {
    setViewData(row);
    setViewModalOpen(true);
  };

  const handleApprove = async () => {
    if (!selectedId) return;
    try {
      setActionLoading(true);
      const payload = { 
        status: 'approved',
        status_updated_by: authData?.user_id 
      };
      await PutApi(`${API_URLS.ProductionCenter}`, { id: selectedId }, payload, token);
      
      setApproveModalOpen(false);
      fetchData(); // ✅ CHANGED: No page argument needed
    } catch (error) {
      console.error('Approval failed:', error);
      alert('Failed to approve center.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedId) return;
    if (!rejectComment.trim()) {
        alert('Please enter a reason for rejection.');
        return;
    }
    
    try {
      setActionLoading(true);
      const payload = { 
        status: 'rejected', 
        rejected_comment: rejectComment,
        status_updated_by: authData?.user_id 
      };
      await PutApi(`${API_URLS.ProductionCenter}`, { id: selectedId }, payload, token);
      
      setRejectModalOpen(false);
      fetchData(); // ✅ CHANGED: No page argument needed
    } catch (error) {
      console.error('Rejection failed:', error);
      alert('Failed to reject center.');
    } finally {
      setActionLoading(false);
    }
  };

  // ✅ REMOVED: handlePageChange function

  const handleRefresh = () => {
    // ✅ CHANGED: Removed setCurrentPage(1)
    fetchData();
    setOpenForm(false);
    setSelectedRegistration(null);
    setActionType('create');
  };

  const handleClearFilters = () => {
      setFilterProductionType(null);
      setFilterStatus('');
      setSelectedDepartments([]);
      setSelectedDistricts([]);
      setSelectedBlocks([]);
      // ✅ CHANGED: Removed setCurrentPage(1)
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  if (loading && registrations.length === 0) {
    return (
      <Box className="dashboard-container" display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <>
      {/* Main Container - Fluid Width */}
      <Box sx={{ pt: 3, pb: 4, px: { xs: 2, sm: 3, md: 4 }, minHeight: '100vh', bgcolor: '#e8f7e0' }}>
          {/* Header Section */}
          <Box mb={3}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: '#1B5E20', letterSpacing: '0.5px',  }}>Production Centers</Typography>
            <Typography variant="body1" color="text.secondary">
              {/* ✅ CHANGED: Use registrations.length since we aren't tracking totalCount anymore */}
              Manage and Approve Production Centers ({registrations.length} total)
            </Typography>
          </Box>

          {/* --- FILTER SECTION --- */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Box display="flex" alignItems="center" mb={2}>
                <FilterListIcon sx={{ mr: 1 }} color="primary" />
                <Typography variant="h6" fontWeight={700}>Filters</Typography>
                <Button 
                    size="small" 
                    startIcon={<RestartAltIcon />} 
                    onClick={handleClearFilters}
                    sx={{ ml: 'auto' }}
                >
                    Clear All
                </Button>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
    {/* Row 1: Type & Status */}
    <Grid item xs={12} sm={6} md={4} lg={2.4} width="200px">
        <FormControl fullWidth size="small">
            <InputLabel sx={{ color: '#000000', fontWeight: 500, '&.Mui-focused': { color: '#000000' } }}>Production Type</InputLabel>
            <Select
                value={filterProductionType || ''}
                label="Production Type"
                onChange={(e) => { setFilterProductionType(e.target.value || null); }}
                sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4caf50',
                        borderWidth: 1.5,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4caf50',
                        borderWidth: 2,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#2e7d32',
                        borderWidth: 2,
                    },
                }}
            >
                <MenuItem value="">
                    <em>All</em>
                </MenuItem>
                <MenuItem value="government">Government</MenuItem>
                <MenuItem value="private">Private</MenuItem>
            </Select>
        </FormControl>
    </Grid>
    <Grid item xs={12} sm={6} md={4} lg={2.4} width="200px">
        <FormControl fullWidth size="small">
            <InputLabel sx={{ color: '#000000', fontWeight: 500, '&.Mui-focused': { color: '#000000' } }}>Status</InputLabel>
            <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => { setFilterStatus(e.target.value); }}
                sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4caf50',
                        borderWidth: 1.5,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4caf50',
                        borderWidth: 2,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#2e7d32',
                        borderWidth: 2,
                    },
                }}
            >
                <MenuItem value="">
                    <em>All</em>
                </MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
        </FormControl>
    </Grid>

    {/* Row 2: Autocomplete Multi-selects */}
    <Grid item xs={12} sm={6} md={4} lg={2.4} width="200px">
        <Autocomplete
            multiple
            size="small"
            options={departmentOptions}
            getOptionLabel={(option) => option.name || option.department_name || ''}
            value={selectedDepartments}
            onChange={(e, newValue) => { setSelectedDepartments(newValue); }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Departments"
                    placeholder="Select..."
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4caf50',
                                borderWidth: 1.5,
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4caf50',
                                borderWidth: 2,
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#2e7d32',
                                borderWidth: 2,
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: '#000000',
                            fontWeight: 500,
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                            color: '#000000',
                        },
                    }}
                />
            )}
        />
    </Grid>
    <Grid item xs={12} sm={6} md={4} lg={2.4} width="200px">
        <Autocomplete
            multiple
            size="small"
            options={districtOptions}
            getOptionLabel={(option) => option.District_Name || option.name || ''}
            value={selectedDistricts}
            onChange={(e, newValue) => { setSelectedDistricts(newValue); }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Districts"
                    placeholder="Select..."
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4caf50',
                                borderWidth: 1.5,
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4caf50',
                                borderWidth: 2,
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#2e7d32',
                                borderWidth: 2,
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: '#000000',
                            fontWeight: 500,
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                            color: '#000000',
                        },
                    }}
                />
            )}
        />
    </Grid>
    <Grid item xs={12} sm={6} md={4} lg={2.4} width="200px">
        <Autocomplete
            multiple
            size="small"
            options={blockOptions}
            getOptionLabel={(option) => option.Block_Name || option.name || ''}
            value={selectedBlocks}
            onChange={(e, newValue) => { setSelectedBlocks(newValue); }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Blocks"
                    placeholder="Select..."
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4caf50',
                                borderWidth: 1.5,
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4caf50',
                                borderWidth: 2,
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#2e7d32',
                                borderWidth: 2,
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: '#000000',
                            fontWeight: 500,
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                            color: '#000000',
                        },
                    }}
                />
            )}
        />
    </Grid>
</Grid>
          </Paper>
          {/* --- END FILTER SECTION --- */}

          {/* Table Section */}
          <Paper sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            <DynamicTable
              data={registrations}
              // ✅ ADDED: defaultRowsPerPage set to 10 to match your previous view
              defaultRowsPerPage={10} 
              columns={[
                { 
                  field: 'production_center_code', 
                  headerName: 'Code',
                  width: 120
                },
                { 
                  field: 'name_of_production_centre', 
                  headerName: 'Center Name',
                  width: 200
                },
                { 
                  field: 'production_type', 
                  headerName: 'Type',
                  width: 120,
                  render: (row) => (
                    <Chip 
                      label={row.production_type?.toUpperCase() || 'N/A'} 
                      size="small" 
                      variant="outlined"
                    />
                  )
                },
                { 
                  field: 'nursery_category', 
                  headerName: 'Type',
                  width: 120,
                  render: (row) => (
                    <Chip 
                      label={row.nursery_category?.toUpperCase() || 'N/A'} 
                      size="small" 
                      variant="outlined"
                    />
                  )
                },
                { 
                  field: 'location', 
                  headerName: 'Location',
                  width: 180,
                  render: (row) => (
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {row.district_name || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Block: {row.block_name || 'N/A'}
                      </Typography>
                    </Box>
                  )
                },
                { 
                  field: 'complete_address', 
                  headerName: 'Address',
                  width: 220,
                  render: (row) => (
                    <Tooltip title={row.complete_address || ''}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap', 
                          maxWidth: 200 
                        }}
                      >
                        {row.complete_address || 'N/A'}
                      </Typography>
                    </Tooltip>
                  )
                },
                { 
                  field: 'contact', 
                  headerName: 'Contact',
                  width: 150,
                  render: (row) => (
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {row.contact_person}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.mobile_number}
                      </Typography>
                    </Box>
                  )
                },
                { 
                  field: 'status', 
                  headerName: 'Status',
                  width: 120,
                  render: (row) => (
                    <Chip 
                      label={row.status?.toUpperCase() || 'PENDING'} 
                      color={getStatusColor(row.status)}
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                  )
                },
                { 
                  field: 'actions', 
                  headerName: 'Actions',
                  width: 180,
                  render: (row) => (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {row.status !== 'approved' && (
                        <Tooltip title="Approve">
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => handleOpenApproveModal(row.id)}
                            sx={{ bgcolor: 'success.lighter', '&:hover': { bgcolor: 'success.light' } }}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {row.status !== 'rejected' && (
                        <Tooltip title="Reject">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleOpenRejectModal(row.id)}
                            sx={{ bgcolor: 'error.lighter', '&:hover': { bgcolor: 'error.light' } }}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      <Tooltip title="Preview Details">
                         <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleOpenViewModal(row)}
                            sx={{ bgcolor: 'primary.lighter', '&:hover': { bgcolor: 'primary.light' } }}
                          >
                           <VisibilityIcon fontSize="small" />
                         </IconButton>
                      </Tooltip>
                    </Box>
                  )
                }
              ]}
              loading={loading}
              emptyMessage="No production centers found"
            />
          </Paper>

          {/* ✅ REMOVED: The old bottom Pagination component is gone. 
              DynamicTable now handles pagination internally! */}

          <RegistrationForm 
            open={openForm}
            onClose={() => {
              setOpenForm(false);
              setSelectedRegistration(null);
              setActionType('create');
            }}
            productionTypes={productionTypes}
            productionCenters={productionCenters}
            selectedRegistration={selectedRegistration}
            actionType={actionType}
            onSuccess={handleRefresh}
          />

          {/* APPROVE Confirmation Modal */}
          <DynamicModal
            open={approveModalOpen}
            title="Confirm Approval"
            contentText="Are you sure you want to APPROVE this production center?"
            onClose={() => setApproveModalOpen(false)}
            onConfirm={handleApprove}
            confirmText="Yes, Approve"
            cancelText="Cancel"
            loading={actionLoading}
          />

          {/* REJECT Comment Modal */}
          <Dialog open={rejectModalOpen} onClose={() => setRejectModalOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ bgcolor: '#fff5f5', color: 'error.main', fontWeight: 700 }}>
              Reject Production Center
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" mb={2} color="text.secondary">
                Please provide a reason for rejection.
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Rejection Reason"
                type="text"
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
              />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setRejectModalOpen(false)} color="inherit" disabled={actionLoading}>
                Cancel
              </Button>
              <Button 
                onClick={handleReject} 
                variant="contained" 
                color="error"
                disabled={!rejectComment.trim() || actionLoading}
                startIcon={actionLoading ? <CircularProgress size={20} /> : <CancelIcon />}
              >
                Confirm Rejection
              </Button>
            </DialogActions>
          </Dialog>

          {/* PREVIEW / VIEW Modal */}
          <DynamicModal
            open={viewModalOpen}
            onClose={() => setViewModalOpen(false)}
            title="Production Center Details"
            maxWidth="md" 
            hideActions={true} 
          >
            {viewData && (
              <Box sx={{ py: 2 }}>
                <Grid container spacing={3}>
                  {/* Basic Info */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Code</Typography>
                    <Typography variant="subtitle1" fontWeight={700}>{viewData.production_center_code || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Center Name</Typography>
                    <Typography variant="subtitle1" fontWeight={700}>{viewData.name_of_production_centre || 'N/A'}</Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Production Type</Typography>
                    <Typography variant="subtitle1">{viewData.production_type || 'N/A'}</Typography>
                  </Grid>
                  

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="overline" color="primary.main" fontWeight={700}>Location Details</Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Department</Typography>
                    <Typography variant="subtitle1">{viewData.department_name || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Village</Typography>
                    <Typography variant="subtitle1">{viewData.village_name || 'N/A'}</Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">District</Typography>
                    <Typography variant="subtitle1">{viewData.district_name || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Block</Typography>
                    <Typography variant="subtitle1">{viewData.block_name || 'N/A'}</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Complete Address</Typography>
                    <Typography variant="subtitle1">{viewData.complete_address || 'N/A'}</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Nursery Category</Typography>
                    <Typography variant="subtitle1">{viewData.nursery_category || 'N/A'}</Typography>
                  </Grid>

                  <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

                  {/* Contact Info */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Contact Person</Typography>
                    <Typography variant="subtitle1">{viewData.contact_person || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Mobile Number</Typography>
                    <Typography variant="subtitle1">{viewData.mobile_number || 'N/A'}</Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Nursery Capacity</Typography>
                    <Typography variant="subtitle1">{viewData.nursery_capacity || 'N/A'}</Typography>
                  </Grid>
                   <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                        <Chip 
                        label={viewData.status?.toUpperCase()} 
                        color={getStatusColor(viewData.status)}
                        size="small"
                        sx={{ fontWeight: 700 }}
                        />
                    </Box>
                  </Grid>

                  {viewData.status === 'rejected' && (
                     <Grid item xs={12}>
                        <Box sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 2, border: '1px solid #ffcdd2' }}>
                            <Typography variant="body2" color="error.dark" fontWeight={700} gutterBottom>
                                Rejection Reason:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {viewData.rejected_comment || 'No comment provided.'}
                            </Typography>
                        </Box>
                     </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </DynamicModal>
      </Box>
    </>
  );
};

export default Production;